// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import { CPT, getReferenceString } from '@medplum/core';
import type { ChargeItem, Claim, ClaimItem, Coverage, Encounter, Reference } from '@medplum/fhirtypes';
import { resolveBillingPathway } from '@polarissleep/shared';
import { calculateTotalPrice } from './chargeitems';
import { createSelfPayCoverage } from './coverage';
import { withTenantScope } from './tenant';

export const CLAIM_PLACE_OF_SERVICE_URL = 'http://polarissleep.com/fhir/extensions/place-of-service';

export async function createClaimFromEncounter(
  medplum: MedplumClient,
  patientId: string,
  encounterId: string,
  practitionerId: string,
  chargeItems: ChargeItem[],
  orgId?: string
): Promise<Claim | undefined> {
  const [patient, encounter] = await Promise.all([
    medplum.readResource('Patient', patientId),
    medplum.readResource('Encounter', encounterId),
  ]);
  const dateOfService = encounter.period?.start;
  if (!dateOfService) {
    return undefined;
  }
  try {
    if (resolveBillingPathway(patient, dateOfService) === 'SELF-PAY') {
      return undefined;
    }
  } catch {
    return undefined;
  }

  const coverageResults = await medplum.searchResources('Coverage', {
    beneficiary: `Patient/${patientId}`,
    status: 'active',
    ...(orgId ? { _compartment: `Organization/${orgId}` } : {}),
  });
  let coverage: Coverage = coverageResults[0];
  if (!coverage) {
    coverage = await createSelfPayCoverage(medplum, patientId, orgId);
  }

  const claim: Claim = {
    resourceType: 'Claim',
    status: 'draft',
    type: { coding: [{ code: 'professional' }] },
    use: 'claim',
    created: new Date().toISOString(),
    extension: [{ url: CLAIM_PLACE_OF_SERVICE_URL, valueCode: '02' }],
    patient: { reference: `Patient/${patientId}` },
    provider: { reference: `Practitioner/${practitionerId}`, type: 'Practitioner' },
    priority: { coding: [{ code: 'normal' }] },
    insurance: [
      {
        sequence: 1,
        focal: true,
        coverage: { reference: getReferenceString(coverage) },
      },
    ],
    item: getCptChargeItems(chargeItems, { reference: `Encounter/${encounterId}` }),
    total: { value: calculateTotalPrice(getCptChargeItemsForTotal(chargeItems)) },
  };
  return medplum.createResource(withTenantScope(claim, orgId));
}

export function getCptChargeItems(chargeItems: ChargeItem[], encounter: Reference<Encounter>): ClaimItem[] {
  const cptChargeItems = chargeItems.filter((item) => item.code?.coding?.some((coding) => coding.system === CPT));
  return cptChargeItems.map((chargeItem: ChargeItem, index: number) => {
    const modifiers = chargeItem.extension
      ?.filter((ext) => ext.url === 'http://hl7.org/fhir/StructureDefinition/chargeitem-modifier')
      .map((ext) => {
        return ext.valueCodeableConcept;
      })
      .filter((modifier) => modifier !== undefined);

    return {
      sequence: index + 1,
      encounter: [encounter],
      productOrService: {
        coding: chargeItem.code.coding?.filter((coding) => coding.system === CPT),
        text: chargeItem.code.text,
      },
      net: chargeItem.priceOverride,
      ...(modifiers && modifiers.length > 0 ? { modifier: modifiers } : {}),
    };
  });
}

export function getCptChargeItemsForTotal(chargeItems: ChargeItem[]): ChargeItem[] {
  return chargeItems.filter((item) => item.code?.coding?.some((coding) => coding.system === CPT));
}
